import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import { amountInWords, buildPaymentQrPayload, encodeCp1251, formatMoney, formatQuantity, sum, toDecimal } from '@erp/shared';
import { invoicesApi } from '../api/invoices';

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
}

/** ИНН/КПП печатаются, только если есть — у организации-физлица (без статуса ИП) их может не быть. */
function partyRequisites(party: { inn: string | null; kpp: string | null }): string {
  const parts: string[] = [];
  if (party.inn) parts.push(`ИНН ${party.inn}`);
  if (party.kpp) parts.push(`КПП ${party.kpp}`);
  return parts.join(', ');
}

/**
 * PDF собирается как «скриншот» уже отрисованного бланка (html2canvas → jsPDF),
 * а не пересобирается заново через API PDF-библиотеки. Так гарантированно
 * выглядит один в один с версией на экране/печати, без дублирования вёрстки —
 * цена в том, что текст в PDF не выделяется и не ищется, это растровая копия.
 */
async function downloadInvoicePdf(element: HTMLElement, filename: string): Promise<void> {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    ignoreElements: (el) => el.classList.contains('no-print'),
  });

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  const imgData = canvas.toDataURL('image/png');

  // Многостраничная разбивка: одно и то же изображение сдвигается вверх на
  // каждой следующей странице — jsPDF обрезает лишнее по границам листа,
  // так что видна только соответствующая полоса.
  let heightLeft = imgHeight;
  let position = 0;
  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;
  while (heightLeft > 0) {
    position -= pageHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }

  pdf.save(filename);
}

/**
 * QR для оплаты по ГОСТ Р 56042-2014 — банковские приложения читают его как
 * CP1251, поэтому кодируем байты вручную через сегмент mode:'byte', а не
 * отдаём библиотеке строку (она закодировала бы кириллицу в UTF-8, и
 * приложение показало бы вместо текста «кракозябры»).
 */
async function buildPaymentQrDataUrl(payload: string): Promise<string> {
  const bytes = Array.from(encodeCp1251(payload));
  return QRCode.toDataURL(
    // Типы qrcode ожидают строку — ручной байтовый сегмент через as never, см. README.
    [{ data: bytes, mode: 'byte' }] as never,
    { errorCorrectionLevel: 'M', margin: 1, width: 220 },
  );
}

/**
 * Отдельный маршрут без AppShell: печатная форма должна занимать весь лист,
 * а не колонку внутри интерфейса. window.print() вызывается автоматически —
 * так же, как открывается системный диалог печати в 1С по кнопке «Печать».
 */
export function InvoicePrintPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const autoDownload = searchParams.get('download') === '1';
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const invoiceQuery = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.get(id!),
    enabled: !!id,
  });

  const invoice = invoiceQuery.data?.invoice;
  const account = invoice?.bankAccount ?? null;
  const org = invoice?.organization;

  useEffect(() => {
    // ИНН получателя обязателен в payload QR по ГОСТ Р 56042-2014 — без него
    // корректный QR не построить, для организации-физлица просто не показываем блок.
    if (!invoice || !account || !org || !org.inn) return;
    let cancelled = false;
    const payload = buildPaymentQrPayload({
      payeeName: org.fullName,
      accountNumber: account.accountNumber,
      bankName: account.bankName,
      bik: account.bik,
      corrAccount: account.corrAccount,
      inn: org.inn,
      kpp: org.kpp,
      sum: invoice.total,
      purpose: `Оплата по счету № ${invoice.number} от ${formatDate(invoice.date)}`,
    });
    buildPaymentQrDataUrl(payload)
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [invoice, account, org]);

  function pdfFilename(): string {
    return `Счёт № ${invoice!.number} от ${formatDate(invoice!.date)}.pdf`;
  }

  async function handleDownload() {
    if (!printRef.current) return;
    setDownloading(true);
    try {
      await downloadInvoicePdf(printRef.current, pdfFilename());
    } finally {
      setDownloading(false);
    }
  }

  // Открытие с ?download=1 (кнопка «Скачать PDF» на странице счёта) сразу
  // формирует файл, без диалога печати; обычное открытие — печать, как раньше.
  useEffect(() => {
    if (!invoice) return;
    const timer = setTimeout(() => {
      if (autoDownload) void handleDownload();
      else window.print();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, autoDownload]);

  if (!invoice || !org) {
    return <div style={{ padding: 24, fontFamily: 'Arial, sans-serif' }}>Загрузка…</div>;
  }

  const cp = invoice.counterparty;
  const totalQuantity = sum(invoice.lines.map((l) => toDecimal(l.quantity)));
  const vatExempt = Number(invoice.vatAmount) === 0;
  const showAccountant = org.kind === 'LEGAL_ENTITY' && !org.accountantIsDirector;
  const payerPosition = org.directorPosition || (org.kind === 'ENTREPRENEUR' ? 'Индивидуальный предприниматель' : 'Руководитель');

  return (
    <div className="invoice-print" ref={printRef}>
      <style>{`
        @page { size: A4; margin: 12mm; }
        .invoice-print {
          font-family: Arial, Helvetica, sans-serif;
          font-size: 11px;
          color: #000;
          max-width: 210mm;
          margin: 0 auto;
          padding: 12mm;
          background: #fff;
        }
        .invoice-print table { border-collapse: collapse; width: 100%; }
        .invoice-print .req-outer td { vertical-align: top; padding: 0; }
        .invoice-print .req-table { border: 1px solid #000; }
        .invoice-print .req-table td { border: 1px solid #000; padding: 3px 6px; }
        .invoice-print .req-table .label { white-space: nowrap; width: 1%; }
        .invoice-print .req-table .caption { font-size: 9px; color: #444; margin-top: 2px; }
        .invoice-print .qr-cell { text-align: center; padding-left: 10px; }
        .invoice-print .qr-caption { font-size: 9px; color: #444; max-width: 110px; margin: 2px auto 0; }
        .invoice-print h1 { font-size: 15px; margin: 14px 0 10px; }
        .invoice-print .party { display: flex; gap: 6px; margin-bottom: 4px; }
        .invoice-print .party .label { width: 130px; flex: none; color: #333; }
        .invoice-print .party .value { font-weight: 700; }
        .invoice-print .items-table th, .invoice-print .items-table td {
          border: 1px solid #000; padding: 3px 6px; font-size: 10.5px;
        }
        .invoice-print .items-table th { background: #f0f0f0; text-align: center; font-weight: 700; }
        .invoice-print .items-table .col-index { text-align: center; font-size: 9px; color: #666; font-weight: 400; background: #fff; }
        .invoice-print .items-table td.num { text-align: right; white-space: nowrap; }
        .invoice-print .items-table tfoot td { border-top: 2px solid #000; font-weight: 700; }
        .invoice-print .totals { margin-top: 8px; text-align: right; }
        .invoice-print .totals .row { display: flex; justify-content: flex-end; gap: 16px; }
        .invoice-print .totals .row .label { min-width: 160px; text-align: left; }
        .invoice-print .words { margin-top: 10px; }
        .invoice-print .words b { font-weight: 700; }
        .invoice-print .footer-text { margin-top: 10px; white-space: pre-line; font-size: 10.5px; }
        .invoice-print .signatures { margin-top: 30px; }
        .invoice-print .sign-row { display: flex; align-items: flex-end; gap: 8px; }
        .invoice-print .sign-line { flex: 1; border-bottom: 1px solid #000; min-width: 220px; }
        .invoice-print .sign-caption-row { display: flex; gap: 8px; font-size: 9px; color: #444; }
        .invoice-print .sign-caption-line { flex: 1; text-align: center; min-width: 220px; }
        .invoice-print .stamp-mark { margin-top: 6px; color: #333; }
        @media screen {
          .invoice-print { box-shadow: 0 0 8px rgba(0,0,0,0.2); margin: 16px auto; }
          body { background: #ddd; }
        }
        @media print {
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print" style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
        <button onClick={() => window.print()} style={{ padding: '6px 14px', cursor: 'pointer' }}>
          Печать
        </button>
        <button onClick={() => void handleDownload()} disabled={downloading} style={{ padding: '6px 14px', cursor: 'pointer' }}>
          {downloading ? 'Формирование PDF…' : 'Скачать PDF'}
        </button>
      </div>

      <table className="req-outer">
        <tbody>
          <tr>
            <td style={{ width: '75%' }}>
              <table className="req-table">
                <tbody>
                  <tr>
                    <td rowSpan={2} style={{ width: '46%' }}>
                      {account ? `${account.bankName}` : 'Расчётный счёт не указан'}
                      <div className="caption">Банк получателя</div>
                    </td>
                    <td className="label">БИК</td>
                    <td>{account?.bik ?? ''}</td>
                  </tr>
                  <tr>
                    <td className="label">Сч. №</td>
                    <td>{account?.corrAccount ?? ''}</td>
                  </tr>
                  <tr>
                    <td>
                      {org.inn ? `ИНН ${org.inn}` : ''}
                      {org.kpp ? ` КПП ${org.kpp}` : ''}
                    </td>
                    <td className="label">Сч. №</td>
                    <td>{account?.accountNumber ?? ''}</td>
                  </tr>
                  <tr>
                    <td colSpan={3}>
                      {org.fullName}
                      <div className="caption">Получатель</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
            <td className="qr-cell" style={{ width: '25%' }}>
              {qrDataUrl && (
                <>
                  <img src={qrDataUrl} width={110} height={110} alt="QR для оплаты" />
                  <div className="qr-caption">Отсканируйте для оплаты</div>
                </>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <h1>
        Счёт на оплату № {invoice.number} от {formatDate(invoice.date)}
      </h1>

      <div className="party">
        <span className="label">Поставщик (исполнитель):</span>
        <span className="value">
          {org.fullName}
          {partyRequisites(org) ? `, ${partyRequisites(org)}` : ''}
          {org.addressFull ? `, ${org.addressFull}` : ''}
        </span>
      </div>
      <div className="party">
        <span className="label">Покупатель (заказчик):</span>
        <span className="value">
          {cp.fullName}
          {cp.inn ? `, ИНН ${cp.inn}` : ''}
          {cp.kpp ? `, КПП ${cp.kpp}` : ''}
        </span>
      </div>

      <table className="items-table">
        <thead>
          <tr>
            <th style={{ width: 24 }}>№</th>
            <th>Товар (услуга)</th>
            <th style={{ width: 90 }}>Код</th>
            <th style={{ width: 55 }}>Кол-во</th>
            <th style={{ width: 45 }}>Ед.</th>
            <th style={{ width: 75 }}>Цена</th>
            <th style={{ width: 90 }}>Сумма</th>
          </tr>
          <tr>
            {[1, 2, 3, 4, 5, 6, 7].map((n) => (
              <th key={n} className="col-index">
                {n}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line) => (
            <tr key={line.id}>
              <td className="num">{line.lineNumber}</td>
              <td>{line.name}</td>
              <td>{line.article ?? ''}</td>
              <td className="num">{formatQuantity(line.quantity)}</td>
              <td>{line.unitSymbol ?? ''}</td>
              <td className="num">{formatMoney(line.price)}</td>
              <td className="num">{formatMoney(line.totalAmount)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} />
            <td className="num">{formatQuantity(totalQuantity)}</td>
            <td colSpan={2} />
            <td className="num">{formatMoney(invoice.total)}</td>
          </tr>
        </tfoot>
      </table>

      <div className="totals">
        <div className="row">
          <span className="label">Итого:</span>
          <span>{formatMoney(invoice.total)}</span>
        </div>
        <div className="row">
          <span className="label">{vatExempt ? 'Без налога (НДС):' : 'В том числе НДС:'}</span>
          <span>{vatExempt ? '—' : formatMoney(invoice.vatAmount)}</span>
        </div>
        <div className="row">
          <span className="label">
            <b>Всего к оплате:</b>
          </span>
          <b>{formatMoney(invoice.total)}</b>
        </div>
      </div>

      <div className="words">
        Всего наименований {invoice.lines.length}, на сумму {formatMoney(invoice.total)} руб.
        <br />
        <b>{amountInWords(invoice.total)}</b>
      </div>

      {invoice.footerText && <div className="footer-text">{invoice.footerText}</div>}

      <div className="signatures">
        <div className="sign-row">
          <span>{payerPosition}</span>
          <span className="sign-line" />
          <span style={{ minWidth: 180 }}>{org.directorName ?? ''}</span>
        </div>
        <div className="sign-caption-row">
          <span style={{ width: payerPosition.length * 6 }} />
          <span className="sign-caption-line">подпись</span>
          <span style={{ minWidth: 180, textAlign: 'center' }}>расшифровка подписи</span>
        </div>
        <div className="stamp-mark">М.П.</div>

        {showAccountant && (
          <>
            <div className="sign-row" style={{ marginTop: 20 }}>
              <span>Главный бухгалтер</span>
              <span className="sign-line" />
              <span style={{ minWidth: 180 }}>{org.accountantName ?? ''}</span>
            </div>
            <div className="sign-caption-row">
              <span style={{ width: 130 }} />
              <span className="sign-caption-line">подпись</span>
              <span style={{ minWidth: 180, textAlign: 'center' }}>расшифровка подписи</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
